import { z } from "zod";

// ---------------------------------------------------------------------------
// Focus goals ("Mål")
//
// One focus goal per department (child sales team): a KPI, where it is now,
// where it should be and by when. Stored at:
//   Businesses/{businessId}/goals/{goalId}
//
// Weekly sub-targets are derived, never stored (`weeklySubTargets`). A locked
// goal is "reached four weeks in a row"; Graftus keeps following it in the
// background and the department picks a new focus.
// ---------------------------------------------------------------------------

export const GoalStatus = z.enum(["active", "archived"]);
export type GoalStatus = z.infer<typeof GoalStatus>;

export interface GoalRecord {
  id: string;
  businessId: string;
  salesTeamId: string;
  kpiDefinitionId: string;
  fromValue: number;
  toValue: number;
  /** YYYY-MM-DD. */
  byDate: string;
  locked: boolean;
  lockedAt: string | null;
  lockedBy: string | null;
  status: GoalStatus;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const ListGoalsInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
  /** Defaults to active only. */
  includeArchived: z.boolean().optional(),
});
export type ListGoalsInput = z.infer<typeof ListGoalsInput>;

export const CreateGoalInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  kpiDefinitionId: z.string().min(1),
  fromValue: z.number(),
  toValue: z.number(),
  byDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type CreateGoalInput = z.infer<typeof CreateGoalInput>;

export const UpdateGoalInput = z.object({
  businessId: z.string().min(1),
  goalId: z.string().min(1),
  kpiDefinitionId: z.string().min(1).optional(),
  fromValue: z.number().optional(),
  toValue: z.number().optional(),
  byDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  status: GoalStatus.optional(),
});
export type UpdateGoalInput = z.infer<typeof UpdateGoalInput>;

export const LockGoalInput = z.object({
  businessId: z.string().min(1),
  goalId: z.string().min(1),
  locked: z.boolean(),
});
export type LockGoalInput = z.infer<typeof LockGoalInput>;

export interface WeeklySubTarget {
  /** Monday of the week, YYYY-MM-DD. */
  weekStart: string;
  target: number;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function mondayOf(d: Date): Date {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() - (day - 1));
  return date;
}

/**
 * Linear weekly sub-targets from the goal's first week (its creation week, or
 * `now` when it has no timestamp yet) to the week of `byDate`, inclusive.
 * Pure, so the home, the goal page and the department page agree.
 */
export function weeklySubTargets(
  goal: Pick<GoalRecord, "fromValue" | "toValue" | "byDate" | "createdAt">,
  now: Date = new Date(),
): WeeklySubTarget[] {
  const start = mondayOf(goal.createdAt ? new Date(goal.createdAt) : now);
  const end = mondayOf(new Date(`${goal.byDate}T00:00:00Z`));
  const weeks = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / (7 * 24 * 3600 * 1000)),
  );
  const out: WeeklySubTarget[] = [];
  for (let i = 0; i <= weeks; i++) {
    const weekStart = new Date(start.getTime() + i * 7 * 24 * 3600 * 1000);
    const t = i / weeks;
    out.push({
      weekStart: isoDate(weekStart),
      target: goal.fromValue + (goal.toValue - goal.fromValue) * t,
    });
  }
  return out;
}
